import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type ContentFieldProvider = {
  currValue: string;
  ref: React.RefObject<HTMLTextAreaElement | null>;
  isUndoable: boolean;
  isRedoable: boolean;
  setCurrValue: (value: string) => void;
  onReplaceContent: (content: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onReset: () => void;
};

const ContentFieldContext = createContext<ContentFieldProvider | null>(null);

type ContentFieldProviderProps = {
  children: React.ReactNode;
};

// Offers utility sets and handle internal states of a content field (e.g. TicketCommentField)
// and be able to directly interact with a nested input in the component tree
export const ContentFieldProvider: React.FC<ContentFieldProviderProps> = ({ children }) => {
  const [currValue, setCurrValue] = useState("");
  const [history, setHistory] = useState([""]);
  const [pointer, setPointer] = useState(0);

  const refInput = useRef<HTMLTextAreaElement>(null);
  const isUndoable = pointer > 0;
  const isRedoable = pointer < history.length - 1;

  useEffect(() => {
    if (history.length > 0) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setCurrValue(history[pointer]);
    }
  }, [history, pointer]);

  const onReset = useCallback(() => {
    setCurrValue("");
    setHistory([]);
    setPointer(0);
  }, []);

  const onReplaceContent = useCallback(
    (content: string) => {
      if (history[pointer] !== content) {
        setHistory((prevHistory) => [...prevHistory, content]);
        setPointer((prevPointer) => prevPointer + 1);
      }

      refInput.current?.focus();
    },
    [history, pointer],
  );

  const onUndo = useCallback(() => {
    if (!isUndoable) return;

    const newPointer = pointer - 1;
    setPointer(newPointer);
  }, [isUndoable, pointer]);

  const onRedo = useCallback(() => {
    if (!isRedoable) return;

    const newPointer = pointer + 1;
    setPointer(newPointer);
  }, [isRedoable, pointer]);

  const onSetCurrValue = useCallback((value: string) => {
    if (!refInput.current) {
      return;
    }

    setCurrValue(value);
    refInput.current.focus();
  }, []);

  const contextValue = useMemo(
    () => ({
      currValue,
      ref: refInput,
      isUndoable,
      isRedoable,
      setCurrValue: onSetCurrValue,
      onReplaceContent,
      onUndo,
      onRedo,
      onReset,
    }),
    [currValue, isUndoable, isRedoable, onSetCurrValue, onReplaceContent, onUndo, onRedo, onReset],
  );

  return <ContentFieldContext value={contextValue}>{children}</ContentFieldContext>;
};

export const useContentFieldManager = (): ContentFieldProvider => {
  const context = useContext(ContentFieldContext);

  if (context === null) {
    throw new Error("useContentFieldManager must be used within a ContentFieldProvider");
  }

  return context;
};
